/**
 * Scheduler Page — Admin Only
 * File: dashboard/src/pages/Scheduler.jsx
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, X, Calendar, Ban, Edit2, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_FULL   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

const STATUS_COLORS = {
  scheduled: 'bg-blue-100 text-blue-700 border border-blue-200',
  running:   'bg-green-100 text-green-700 border border-green-200',
  completed: 'bg-gray-100 text-gray-500 border border-gray-200',
  cancelled: 'bg-red-100 text-red-600 border border-red-200',
  failed:    'bg-red-200 text-red-800 border border-red-300',
};
const STATUS_ICONS = {
  scheduled:'⏳', running:'▶️', completed:'✅', cancelled:'❌', failed:'💥'
};

const EMPTY_FORM = {
  campaign_id: '',
  name: '',
  numbers_list: '',
  sip_trunk_id: '',
  caller_id: '',
  dial_prefix: '',
  ivr_intro_file: '',
  ivr_outro_file: '',
  dtmf_digit: '1',
  concurrent_calls: 10,
  notifications_chat_id: '',
  // New recurring fields
  start_date: '',
  end_date: '',
  run_days: [1, 2, 3, 4, 5], // Mon–Fri default
  daily_start_time: '09:00',
  daily_end_time: '18:00',
  amd_enabled: false,
};

export default function Scheduler() {
  const { user: currentUser } = useAuth();
  const isReadOnly = currentUser?.role === 'viewer';
  const [schedules, setSchedules] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [trunks, setTrunks]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  // ── Load ───────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, t] = await Promise.all([
        api.get('/scheduler'),
        api.get('/scheduler/campaigns/list'),
        api.get('/scheduler/trunks/list'),
      ]);
      setSchedules(s.data.data || []);
      setCampaigns(c.data.data || []);
      setTrunks(t.data.data   || []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Pre-fill from campaign ─────────────────────────────────────────────
  function prefillFromCampaign(campaignId) {
    const c = campaigns.find(x => String(x.id) === String(campaignId));
    if (!c) return;
    setForm(f => ({
      ...f,
      campaign_id:           campaignId,
      name:                  f.name || c.campaign_name,
      sip_trunk_id:          c.sip_trunk_id          || '',
      caller_id:             c.caller_id             || '',
      dial_prefix:           c.dial_prefix           || '',
      ivr_intro_file:        c.ivr_intro_file        || '',
      ivr_outro_file:        c.ivr_outro_file        || '',
      dtmf_digit:            c.dtmf_digit            || '1',
      concurrent_calls:      c.concurrent_calls      || 10,
      notifications_chat_id: c.notifications_chat_id || '',
      amd_enabled:           !!c.amd_enabled,
    }));
  }

  // ── Day toggle ─────────────────────────────────────────────────────────
  function toggleDay(d) {
    setForm(f => ({
      ...f,
      run_days: f.run_days.includes(d)
        ? f.run_days.filter(x => x !== d)
        : [...f.run_days, d].sort((a,b) => a-b)
    }));
  }

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── Open create ────────────────────────────────────────────────────────
  function openCreate() {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setError('');
    setShowModal(true);
  }

  // ── Open edit ──────────────────────────────────────────────────────────
  function openEdit(s) {
    let numbersText = '';
    try {
      const arr = typeof s.numbers_list === 'string'
        ? JSON.parse(s.numbers_list) : (s.numbers_list || []);
      numbersText = arr.map(n => n.phoneNumber || n).join('\n');
    } catch { numbersText = ''; }

    let runDays = [1,2,3,4,5];
    try {
      runDays = typeof s.run_days === 'string'
        ? JSON.parse(s.run_days) : (s.run_days || [1,2,3,4,5]);
    } catch {}

    setForm({
      campaign_id:           s.campaign_id            || '',
      name:                  s.name                   || '',
      numbers_list:          numbersText,
      sip_trunk_id:          s.sip_trunk_id           || '',
      caller_id:             s.caller_id              || '',
      dial_prefix:           s.dial_prefix            || '',
      ivr_intro_file:        s.ivr_intro_file         || '',
      ivr_outro_file:        s.ivr_outro_file         || '',
      dtmf_digit:            s.dtmf_digit             || '1',
      concurrent_calls:      s.concurrent_calls       || 10,
      notifications_chat_id: s.notifications_chat_id  || '',
      start_date:            s.start_date             || '',
      end_date:              s.end_date               || '',
      run_days:              runDays,
      daily_start_time:      s.daily_start_time       || '09:00',
      daily_end_time:        s.daily_end_time         || '18:00',
      amd_enabled:           !!s.amd_enabled,
    });
    setEditingId(s.id);
    setError('');
    setShowModal(true);
  }

  // ── Save ───────────────────────────────────────────────────────────────
  async function handleSave() {
    setError('');
    if (!form.campaign_id || !form.name || !form.sip_trunk_id ||
        !form.caller_id || !form.start_date || !form.daily_start_time || !form.daily_end_time) {
      setError('Campaign, name, trunk, caller ID, start date and time window are required.');
      return;
    }
    if (form.run_days.length === 0) {
      setError('Select at least one day of the week.');
      return;
    }
    if (form.daily_start_time >= form.daily_end_time) {
      setError('Daily end time must be after start time.');
      return;
    }
    if (form.end_date && form.end_date < form.start_date) {
      setError('End date cannot be before start date.');
      return;
    }

    setSaving(true);
    try {
      // Build numbers array
      let numbers_list = [];
      if (typeof form.numbers_list === 'string' && form.numbers_list.trim()) {
        numbers_list = form.numbers_list
          .split(/[\n,;]+/)
          .map(n => n.trim().replace(/\s/g, ''))
          .filter(Boolean)
          .map(n => ({ phoneNumber: n.startsWith('+') ? n : `+${n}`, rawLine: n }));
      }

      const payload = {
        ...form,
        numbers_list:  JSON.stringify(numbers_list),
        run_days:      JSON.stringify(form.run_days),
        // Keep start_datetime for backward compat — set to start_date + daily_start_time
        start_datetime: `${form.start_date}T${form.daily_start_time}:00`,
      };

      if (editingId) {
        await api.put(`/scheduler/${editingId}`, payload);
      } else {
        await api.post('/scheduler', payload);
      }
      setShowModal(false);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleCancel(id) {
    if (!window.confirm('Cancel this schedule?')) return;
    try { await api.post(`/scheduler/${id}/cancel`); load(); }
    catch (e) { alert(e?.response?.data?.error || e.message); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this record permanently?')) return;
    try { await api.delete(`/scheduler/${id}`); load(); }
    catch (e) { alert(e?.response?.data?.error || e.message); }
  }

  // ── Run days summary ───────────────────────────────────────────────────
  function runDaysSummary(days) {
    if (!days) return '—';
    let arr;
    try { arr = typeof days === 'string' ? JSON.parse(days) : days; } catch { return '—'; }
    if (arr.length === 7) return 'Every day';
    if (JSON.stringify(arr) === JSON.stringify([1,2,3,4,5])) return 'Mon – Fri';
    if (JSON.stringify(arr) === JSON.stringify([0,6])) return 'Weekends';
    return arr.map(d => DAY_LABELS[d]).join(', ');
  }

  const countNumbers = t => t ? t.split(/[\n,;]+/).filter(n=>n.trim()).length : 0;

  return (
    <div className="space-y-6">

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Scheduler</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Campaigns run automatically on selected weekdays within the date range and daily time window.
            The Telegram bot process must be running.
          </p>
        </div>
        {!isReadOnly && (
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> New Schedule
          </button>
        )}
      </div>

      {error && !showModal && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center justify-between">
          {error}<button onClick={() => setError('')}><X className="w-4 h-4"/></button>
        </div>
      )}

      {/* ── Table ──────────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin"/>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {schedules.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-40"/>
              <p className="font-medium text-gray-500">No scheduled campaigns yet</p>
              <p className="text-sm mt-1">Click "New Schedule" to create your first one</p>
            </div>
          ) : (
            <table className="min-w-full text-sm divide-y divide-gray-100">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  {['Name','Days & Window','Date Range','Numbers','Status','Results','Actions'].map(h=>(
                    <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {schedules.map(s => (
                  <React.Fragment key={s.id}>
                    <tr className="hover:bg-gray-50 transition-colors">

                      {/* Name */}
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{s.name}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{s.campaign_name}</div>
                      </td>

                      {/* Days & window */}
                      <td className="px-4 py-3">
                        <div className="text-gray-700 font-medium">{runDaysSummary(s.run_days)}</div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {s.daily_start_time || '09:00'} – {s.daily_end_time || '18:00'}
                        </div>
                      </td>

                      {/* Date range */}
                      <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                        <div>{s.start_date || '—'}</div>
                        {s.end_date && <div className="text-gray-400">→ {s.end_date}</div>}
                        {s.last_run_date && (
                          <div className="text-blue-400 mt-0.5">Last ran: {s.last_run_date}</div>
                        )}
                      </td>

                      {/* Numbers */}
                      <td className="px-4 py-3 text-gray-600">
                        {s.processed_numbers > 0
                          ? `${s.processed_numbers} / ${s.total_numbers}`
                          : s.total_numbers || 0}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[s.status] || 'bg-gray-100'}`}>
                          {STATUS_ICONS[s.status]} {s.status}
                        </span>
                        {s.last_error && (
                          <div className="text-xs text-red-500 mt-0.5 max-w-[140px] truncate" title={s.last_error}>
                            ⚠️ {s.last_error}
                          </div>
                        )}
                      </td>

                      {/* Results */}
                      <td className="px-4 py-3">
                        {s.successful_calls > 0 || s.failed_calls > 0 ? (
                          <div className="flex gap-2 text-xs">
                            <span className="text-green-600 font-medium">✅ {s.successful_calls}</span>
                            <span className="text-red-500 font-medium">❌ {s.failed_calls}</span>
                            <span className="text-blue-600 font-medium">🔢 {s.dtmf_responses}</span>
                          </div>
                        ) : <span className="text-gray-400 text-xs">—</span>}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setExpandedRow(expandedRow === s.id ? null : s.id)}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                            title="Details"
                          >
                            {expandedRow === s.id ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}
                          </button>
                          {!isReadOnly && ['scheduled'].includes(s.status) && (
                            <button onClick={() => openEdit(s)}
                              className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded" title="Edit">
                              <Edit2 className="w-4 h-4"/>
                            </button>
                          )}
                          {!isReadOnly && ['scheduled','running'].includes(s.status) && (
                            <button onClick={() => handleCancel(s.id)}
                              className="p-1.5 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50 rounded" title="Cancel">
                              <Ban className="w-4 h-4"/>
                            </button>
                          )}
                          {!isReadOnly && ['scheduled','cancelled','completed','failed'].includes(s.status) && (
                            <button onClick={() => handleDelete(s.id)}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                              <Trash2 className="w-4 h-4"/>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded row */}
                    {expandedRow === s.id && (
                      <tr className="bg-blue-50/30">
                        <td colSpan={7} className="px-6 py-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-gray-600">
                            <div><span className="font-medium text-gray-700 block">Caller ID</span>{s.caller_id||'—'}</div>
                            <div><span className="font-medium text-gray-700 block">Trunk</span>{s.trunk_name||s.sip_trunk_id}</div>
                            <div><span className="font-medium text-gray-700 block">Dial Prefix</span>{s.dial_prefix||'None'}</div>
                            <div><span className="font-medium text-gray-700 block">Concurrent</span>{s.concurrent_calls}</div>
                            <div><span className="font-medium text-gray-700 block">DTMF Digit</span>{s.dtmf_digit}</div>
                            <div><span className="font-medium text-gray-700 block">AMD</span>{s.amd_enabled?'✅ On':'❌ Off'}</div>
                            <div><span className="font-medium text-gray-700 block">Intro File</span>{s.ivr_intro_file||'None'}</div>
                            <div><span className="font-medium text-gray-700 block">Outro File</span>{s.ivr_outro_file||'None'}</div>
                            <div><span className="font-medium text-gray-700 block">Notification Chat</span>{s.notifications_chat_id||'Campaign default'}</div>
                            <div><span className="font-medium text-gray-700 block">Created By</span>{s.created_by}</div>
                          </div>
                          {s.last_error && (
                            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                              <span className="font-medium">Last error:</span> {s.last_error}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Modal ──────────────────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[94vh] flex flex-col">

            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-base font-semibold text-gray-900">
                {editingId ? '✏️ Edit Schedule' : '📅 New Scheduled Campaign'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded text-gray-400">
                <X className="w-5 h-5"/>
              </button>
            </div>

            <div className="overflow-y-auto px-6 py-5 space-y-5 flex-1">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
              )}

              {/* Campaign */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Campaign *</label>
                <select
                  value={form.campaign_id}
                  onChange={e => { setField('campaign_id', e.target.value); prefillFromCampaign(e.target.value); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select campaign…</option>
                  {campaigns.map(c => <option key={c.id} value={c.id}>{c.campaign_name}</option>)}
                </select>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Name *</label>
                <input
                  value={form.name}
                  onChange={e => setField('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                  placeholder="April Morning Calls"
                />
              </div>

              {/* ── Date Range ─────────────────────────────────────── */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date Range *</label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                    <input type="date"
                      value={form.start_date}
                      onChange={e => setField('start_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">End Date <span className="text-gray-400">(optional)</span></label>
                    <input type="date"
                      value={form.end_date}
                      onChange={e => setField('end_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">Leave blank to run indefinitely</p>
                  </div>
                </div>
              </div>

              {/* ── Days of Week ───────────────────────────────────── */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Run on Days *</label>
                <div className="flex gap-2 flex-wrap">
                  {DAY_LABELS.map((label, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => toggleDay(i)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border-2 transition-colors ${
                        form.run_days.includes(i)
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {/* Quick presets */}
                <div className="flex gap-2 mt-2">
                  {[
                    { label: 'Weekdays', days: [1,2,3,4,5] },
                    { label: 'Weekends', days: [0,6] },
                    { label: 'Every day', days: [0,1,2,3,4,5,6] },
                  ].map(p => (
                    <button key={p.label} type="button"
                      onClick={() => setField('run_days', p.days)}
                      className="text-xs px-2 py-1 border border-gray-200 rounded text-gray-500 hover:bg-gray-50"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Daily Time Window ──────────────────────────────── */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Daily Calling Window *</label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Start calling at</label>
                    <input type="time"
                      value={form.daily_start_time}
                      onChange={e => setField('daily_start_time', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Stop calling at</label>
                    <input type="time"
                      value={form.daily_end_time}
                      onChange={e => setField('daily_end_time', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  The campaign fires once per eligible day at the start time. No new calls will be initiated after the end time.
                </p>
              </div>

              {/* ── Summary box ───────────────────────────────────── */}
              {form.start_date && form.run_days.length > 0 && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                  <span className="font-medium">Schedule summary: </span>
                  Runs {runDaysSummary(form.run_days)} from {form.start_date}
                  {form.end_date ? ` to ${form.end_date}` : ' onwards'},
                  calling between {form.daily_start_time} and {form.daily_end_time} each day.
                </div>
              )}

              {/* ── Trunk + Caller ID ─────────────────────────────── */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SIP Trunk *</label>
                  <select value={form.sip_trunk_id} onChange={e => setField('sip_trunk_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500">
                    <option value="">Select trunk…</option>
                    {trunks.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Caller ID *</label>
                  <input value={form.caller_id} onChange={e => setField('caller_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                    placeholder="+441234567890"/>
                </div>
              </div>

              {/* ── Prefix + Concurrent + DTMF ────────────────────── */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dial Prefix</label>
                  <input value={form.dial_prefix} onChange={e => setField('dial_prefix', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500" placeholder="001"/>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Concurrent Calls</label>
                  <input type="number" min="1" max="100" value={form.concurrent_calls}
                    onChange={e => setField('concurrent_calls', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"/>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">DTMF Digit</label>
                  <input value={form.dtmf_digit} maxLength={1} onChange={e => setField('dtmf_digit', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"/>
                </div>
              </div>

              {/* ── IVR + Notification ───────────────────────────── */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Intro File</label>
                  <input value={form.ivr_intro_file} onChange={e => setField('ivr_intro_file', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500" placeholder="intro.wav"/>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Outro File</label>
                  <input value={form.ivr_outro_file} onChange={e => setField('ivr_outro_file', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500" placeholder="outro.wav"/>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notification Chat ID</label>
                  <input value={form.notifications_chat_id} onChange={e => setField('notifications_chat_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500" placeholder="-4801463214"/>
                </div>
              </div>

              {/* ── AMD toggle ───────────────────────────────────── */}
              <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                <button type="button" onClick={() => setField('amd_enabled', !form.amd_enabled)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.amd_enabled ? 'bg-primary-600' : 'bg-gray-200'}`}>
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${form.amd_enabled ? 'translate-x-4' : 'translate-x-0.5'}`}/>
                </button>
                <div>
                  <div className="text-sm font-medium text-gray-700">AMD — Answering Machine Detection</div>
                  <div className="text-xs text-gray-400">Auto-hang up or leave message when voicemail detected</div>
                </div>
              </div>

              {/* ── Phone numbers ────────────────────────────────── */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Numbers
                  <span className="ml-2 font-normal text-gray-400">(one per line)</span>
                </label>
                <textarea
                  value={form.numbers_list}
                  onChange={e => setField('numbers_list', e.target.value)}
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500"
                  placeholder={"+14155551234\n+14155559876\n+447700900123"}
                />
                <p className="text-xs text-gray-400 mt-1">
                  {countNumbers(form.numbers_list)} numbers — these will be dialed on each eligible day
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="px-5 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 font-medium flex items-center gap-2">
                {saving ? 'Saving…' : editingId ? <><Edit2 className="w-4 h-4"/> Update</> : <><Calendar className="w-4 h-4"/> Create Schedule</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}